<template>
  <div id="player-general-performance-table">
    <v-data-table
      :headers="formattedHeaders"
      :loading="loading"
      :items="playerPerformance"
      hide-default-footer
      dense
      disable-pagination
      :custom-sort="customSort"
      sort-by="last_name"
      :sort-desc="true"
    >
      <template v-slot:item="{ item }">
        <tr
          class="clickable"
          @click="$emit('select:player', item)"
        >
          <td
            style="width: 0px"
            class="pr-0"
          >
            <v-avatar
              size="32px"
              class="mr-2"
              color="grey lighten-2"
            >
              <v-img :src="item.photo_url">
                <template v-slot:placeholder>
                  <v-icon x-small>
                    fal fa-user
                  </v-icon>
                </template>
              </v-img>
            </v-avatar>
          </td>
          <td class="text-no-wrap">
            {{ item.last_name }} {{ item.first_name }}
          </td>
          <td
            v-for="tableHeader in tableHeaders.slice(2)"
            :key="tableHeader.value + tableHeader.aggregation"
          >
            <div v-if="wellnessChipVariables.includes(tableHeader.value)">
              <table-bar-value
                v-if="referenceStats"
                :value="item.player_stats ? item.player_stats[tableHeader.value] : undefined"
                :max="100"
                :color="getColorFromValue(item.player_stats ? item.player_stats[tableHeader.value] : undefined, 100,)"
              >
                {{ item.player_stats ? item.player_stats[tableHeader.value] : undefined }}%
              </table-bar-value>
              <table-selection-chip-value
                v-else
                :value="item.player_stats ? item.player_stats[tableHeader.value] : undefined"
                :max="5"
                :reverse-colors="reversedWellnessVariables.includes(tableHeader.value)"
              />
            </div>

            <div v-else-if="tableHeader.value === 'SLD'">
              <table-bar-value
                :value="item.player_stats ? item.player_stats[tableHeader.value] : undefined"
                :max="8"
                color="grey lighten-2"
              >
                {{ item.player_stats ? item.player_stats[tableHeader.value] : undefined }}
              </table-bar-value>
            </div>

            <div v-else-if="tableHeader.value === 'pain' || tableHeader.value.includes('pain')">
              <pain-label
                :value="item.player_stats && item.player_stats.highest_pain ? item.player_stats.highest_pain.value : 0"
                :player-stats="item.player_stats"
                :gender="item.gender"
                stats-suffix=""
              />
            </div>

            <div v-else-if="tableHeader.value === 'sickness' || tableHeader.value.includes('sickness')">
              <sickness-label :player-stats="item.player_stats" />
            </div>

            <div v-else-if="tableHeader.value === 'FMENSACT'">
              <menstrual-phases
                v-if="item.player_stats.cycle_phase && item.player_stats.cycle_phase !== noPhase"
                :current-menstrual-phase="item.player_stats.cycle_phase"
                :stats="item.player_stats"
              />
              <span
                v-else
                class="grey--text caption"
              >
                N/A
              </span>
            </div>

            <div v-else-if="tableHeader.value === 'FMENSFR'">
              <div
                v-if="item.player_stats.FMENSACT !== undefined"
              >
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-chip
                      label
                      outlined
                      small
                      :color="item.player_stats.FMENSACT ? 'blue' : 'grey'"
                      v-on="on"
                    >
                      {{ item.player_stats.FMENSACT ? $t('status.yes') : $t('status.no') }}
                      <span
                        v-if="item.player_stats.FMENSFR"
                        class="ml-1"
                      >
                        ({{ item.player_stats.FMENSFR }})
                      </span>
                    </v-chip>
                  </template>
                  <div>
                    <div v-if="item.player_stats.FMENSFR">
                      {{ $t('individual_analysis.menstrual_period.variable.flow_rate') }} <strong>{{ item.player_stats.FMENSFR }}</strong>
                    </div>
                  </div>
                </v-tooltip>
              </div>
              <div
                v-else
                class="grey--text caption"
              >
                N/A
              </div>
            </div>

            <div v-else-if="gradingVariables.includes(tableHeader.value)">
              <table-bar-value
                v-if="referenceStats"
                :value="item.player_stats ? item.player_stats[tableHeader.value] : undefined"
                :max="100"
                :color="getColorFromValue(item.player_stats ? item.player_stats[tableHeader.value] : undefined, 100)"
              >
                {{ item.player_stats ? item.player_stats[tableHeader.value] : undefined }}%
              </table-bar-value>
              <table-selection-chip-value
                v-else
                :value="item.player_stats ? item.player_stats[tableHeader.value] : undefined"
                :max="tableHeader.value === 'ENJY' ? 5 : 10"
                :reverse-colors="reversedVariables().includes(tableHeader.value)"
              />
            </div>

            <div v-else-if="tableHeader.value === 'reason'">
              <v-chip
                v-if="item.has_attended"
                label
                color="success"
                small
              >
                {{ $t('attended.label') }}
              </v-chip>
              <v-chip
                v-else-if="item.has_attended === null"
                label
                color="grey lighten-3"
                text-color="grey darken-1"
                small
              >
                {{ $t('event_details.no_feedback') }}
              </v-chip>
              <v-chip
                v-else-if="item.has_attended === false"
                label
                color="error"
                dark
                small
              >
                {{ $t('card_title.did_not_attend') }}:
                <strong class="ml-1">{{ item.event_missed_reason ? item.event_missed_reason.reason : 'Unknown reason' }}</strong>
              </v-chip>
              <div v-else>
                N/A
              </div>
            </div>
            <div v-else>
              <table-bar-value
                :value="item.player_stats ? item.player_stats[tableHeader.value] : undefined"
                :max="colorsMaxValues[tableHeader.value] || maxValues[tableHeader.value]"
                :color="tableHeader.colorable ? getColorFromValue(item.player_stats ? item.player_stats[tableHeader.value] : undefined, colorsMaxValues[tableHeader.value] || maxValues[tableHeader.value], reversedVariables().includes(tableHeader.value)) : 'grey lighten-2'"
              >
                {{ item.player_stats ? item.player_stats[tableHeader.value] : undefined }}{{ referenceStats ? '%' : '' }}
              </table-bar-value>
            </div>
          </td>
        </tr>
      </template>
      <template v-slot:loading>
        <div
          class="pa-5 d-flex justify-center align-center"
          :style="'min-height: ' + (playerPerformance.length * 49)+'px'"
        >
          <v-progress-circular
            indeterminate
            size="48"
            width="4"
            color="grey lighten-2"
          />
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>

import TableBarValue from '@/components/helpers/TableBarValue'
import { getColorForValue } from '@/utils/helpers'
import { reversedVariables } from '@/data/models/TableHeader'
import PainLabel from '@/components/helpers/PainLabel.vue'
import TableSelectionChipValue from '@/components/helpers/TableSelectionChipValue.vue'
import SicknessLabel from '@/components/helpers/SicknessLabel.vue'
import MenstrualPhases from '@/components/helpers/MenstrualPhases.vue'

export default {
  name: 'PerformanceGeneralTable',
  components: {
    TableSelectionChipValue,
    PainLabel,
    TableBarValue,
    SicknessLabel,
    MenstrualPhases
  },
  props: {
    playerPerformance: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    maxValues: {
      type: Object,
      required: true
    },
    tableHeaders: {
      type: Array,
      required: true
    },
    colorsMaxValues: {
      type: Object,
      default: () => ({})
    },
    eventTable: {
      type: Boolean,
      default: false
    },
    referenceStats: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({

  }),
  computed: {
    formattedHeaders() {
      return this.tableHeaders.map(header => {
        let minWidth = header.text?.length < 8 ? '10%' : 'auto'

        if (header.value === 'FMENSACT') {
          minWidth = '150px'
        }

        return {
          ...header,
          width: minWidth
        }
      })
    },
    gradingVariables() {
      return ['TG', 'ENJY']
    },
    wellnessChipVariables() {
      return ['FAT', 'GMS', 'STL', 'MOO', 'SLQ']
    },
    reversedWellnessVariables() {
      return ['FAT', 'GMS', 'STL']
    }
  },
  created() {
    this.noPhase = MenstrualPhases.noPhase
  },
  methods: {
    reversedVariables() {
      return reversedVariables
    },
    getColorFromValue(value, max, reverse = false) {
      return getColorForValue(value, max, reverse)
    },
    getValue(player, variable) {
      return player.player_stats[variable]
    },
    customSort(items, index, isDesc) {
      items.sort((a, b) => {
        let aValue = null
        let bValue = null

        if (index[0] === 'last_name') {
          aValue = a.last_name
          bValue = b.last_name
        } else {
          aValue = a.player_stats[index]
          bValue = b.player_stats[index]
        }

        if (isDesc[0]) {
          if (aValue === null || aValue === undefined) return -1
          if (bValue === null || bValue === undefined) return 1
          return aValue > bValue ? 1 : -1
        } else {
          if (aValue === null || aValue === undefined) return 1
          if (bValue === null || bValue === undefined) return -1
          return aValue < bValue ? 1 : -1
        }
      })
      return items
    }
  }
}
</script>
